(() => {
  const tagName = 'duori-subscribe-save';
  if (customElements.get(tagName)) return;

  const hasRenderableSections = (sections, sectionIds) =>
    !!sections &&
    sectionIds.length > 0 &&
    sectionIds.every((sectionId) => typeof sections[sectionId] === 'string' && sections[sectionId].trim() !== '');

  class DuoriSubscribeSave extends HTMLElement {
    constructor() {
      super();

      this.requiredCount = 1;
      this.paidCount = 1;
      this.freeCount = 0;
      this.purchaseType = 'subscribe';
      this.slots = [];
      this.activeSlotIndex = null;

      this.cards = Array.from(this.querySelectorAll('[data-dss-product-card]'));
      this.offerInputs = Array.from(this.querySelectorAll('[data-dss-offer]'));
      this.purchaseInputs = Array.from(this.querySelectorAll('[data-dss-purchase]'));
      this.builderElement = this.querySelector('[data-dss-builder]');
      this.slotContainer = this.querySelector('[data-dss-slots]');
      this.progressElement = this.querySelector('[data-dss-progress]');
      this.progressLabelElement = this.querySelector('[data-dss-progress-label]');
      this.builderHintElement = this.querySelector('[data-dss-builder-hint]');
      this.mainImage = this.querySelector('[data-dss-main-image]');
      this.titleElement = this.querySelector('[data-dss-product-title]');
      this.introElement = this.querySelector('[data-dss-product-intro]');
      this.cartButton = this.querySelector('[data-dss-cart-button]');
      this.cartText = this.querySelector('[data-dss-cart-text]');
      this.errorElement = this.querySelector('[data-dss-error]');
      this.cartDrawerElement = document.querySelector('cart-drawer');
      this.selectedCard = this.cards.find((card) => card.classList.contains('is-active')) || this.cards[0];

      this.handleProductClick = this.handleProductClick.bind(this);
      this.handleOfferChange = this.handleOfferChange.bind(this);
      this.handlePurchaseChange = this.handlePurchaseChange.bind(this);
      this.handleSlotClick = this.handleSlotClick.bind(this);
      this.handleSubmit = this.handleSubmit.bind(this);
    }

    connectedCallback() {
      this.cards.forEach((card) => card.addEventListener('click', this.handleProductClick));
      this.offerInputs.forEach((input) => input.addEventListener('change', this.handleOfferChange));
      this.purchaseInputs.forEach((input) => input.addEventListener('change', this.handlePurchaseChange));
      this.slotContainer?.addEventListener('click', this.handleSlotClick);
      this.cartButton?.addEventListener('click', this.handleSubmit);
      this.syncOfferFromInputs(true);
      this.syncPurchaseFromInputs();
      this.render();
    }

    disconnectedCallback() {
      this.cards.forEach((card) => card.removeEventListener('click', this.handleProductClick));
      this.offerInputs.forEach((input) => input.removeEventListener('change', this.handleOfferChange));
      this.purchaseInputs.forEach((input) => input.removeEventListener('change', this.handlePurchaseChange));
      this.slotContainer?.removeEventListener('click', this.handleSlotClick);
      this.cartButton?.removeEventListener('click', this.handleSubmit);
    }

    formatMoney(cents) {
      if (window.FoxTheme?.Currency && FoxTheme.settings?.moneyFormat) {
        return FoxTheme.Currency.formatMoney(cents || 0, FoxTheme.settings.moneyFormat);
      }

      return new Intl.NumberFormat(document.documentElement.lang || 'en-US', {
        style: 'currency',
        currency: this.dataset.currency || 'USD',
      }).format((cents || 0) / 100);
    }

    formatCompactMoney(cents) {
      return this.formatMoney(cents).replace(/([.,]00)(?!\d)/, '');
    }

    escapeHTML(value) {
      const escapeMap = { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#039;' };
      return String(value || '').replace(/[&<>"']/g, (character) => escapeMap[character]);
    }

    syncOfferFromInputs(seedSlots = false) {
      const input = this.offerInputs.find((item) => item.checked) || this.offerInputs[0];
      const previousUnits = this.getSelectedUnits();

      this.requiredCount = Number(input?.dataset.requiredCount || 1);
      this.paidCount = Number(input?.dataset.paidCount || 1);
      this.freeCount = Number(input?.dataset.freeCount || 0);

      if (this.requiredCount > 1) {
        this.rebuildSlots(seedSlots ? [this.getSelectedUnit()] : previousUnits);
      } else {
        this.slots = [];
        this.activeSlotIndex = null;
      }
    }

    syncPurchaseFromInputs() {
      this.purchaseType = (this.purchaseInputs.find((item) => item.checked) || this.purchaseInputs[0])?.value || 'subscribe';
    }

    getUnitFromCard(card = this.selectedCard || this.cards[0]) {
      return {
        id: String(card?.dataset.variantId || ''),
        title: card?.dataset.title || '',
        shortTitle: card?.dataset.shortTitle || card?.dataset.title || '',
        handle: card?.dataset.handle || '',
        image: card?.dataset.image || '',
        price: Number(card?.dataset.price || 0),
        comparePrice: Number(card?.dataset.comparePrice || card?.dataset.price || 0),
        subPrice: Number(card?.dataset.subPrice || 0),
        subComparePrice: Number(card?.dataset.subComparePrice || card?.dataset.price || 0),
        sellingPlanId: card?.dataset.sellingPlan || '',
        available: card?.dataset.available === 'true',
      };
    }

    getSelectedUnit() {
      return this.getUnitFromCard(this.selectedCard || this.cards[0]);
    }

    getSelectedUnits() {
      if (this.requiredCount === 1) return [this.getSelectedUnit()].filter((unit) => unit.id);
      return this.slots.map((slot) => slot.unit).filter(Boolean);
    }

    rebuildSlots(preservedUnits = []) {
      const units = Array.isArray(preservedUnits) ? preservedUnits.filter(Boolean).slice(0, this.requiredCount) : [];

      this.slots = Array.from({ length: this.requiredCount }, (_, index) => ({
        unit: units[index] || null,
        isFree: index >= this.paidCount,
      }));
      this.activeSlotIndex = this.getFirstEmptySlotIndex();
      if (this.activeSlotIndex < 0) this.activeSlotIndex = 0;
    }

    getFirstEmptySlotIndex() {
      return this.slots.findIndex((slot) => !slot.unit);
    }

    addUnitToSlot(unit) {
      if (this.requiredCount === 1) return;
      if (!this.slots.length) this.rebuildSlots();

      const targetIndex =
        this.activeSlotIndex !== null && this.slots[this.activeSlotIndex]
          ? this.activeSlotIndex
          : this.getFirstEmptySlotIndex();

      if (targetIndex < 0) {
        this.showError(`This offer includes ${this.requiredCount} patches. Tap a filled slot to swap it.`);
        return;
      }

      this.slots[targetIndex].unit = unit;
      const nextEmpty = this.getFirstEmptySlotIndex();
      this.activeSlotIndex = nextEmpty >= 0 ? nextEmpty : targetIndex;
    }

    getUnitPrice(unit, purchaseType = this.purchaseType) {
      if (purchaseType === 'subscribe' && unit.subPrice) return unit.subPrice;
      return unit.price;
    }

    getUnitComparePrice(unit, purchaseType = this.purchaseType) {
      if (purchaseType === 'subscribe' && unit.subComparePrice) return unit.subComparePrice;
      return unit.comparePrice || unit.price;
    }

    calculateTotals(purchaseType = this.purchaseType, offer = null, unitsOverride = null) {
      const activeOffer = offer || { requiredCount: this.requiredCount, paidCount: this.paidCount, freeCount: this.freeCount };
      let units = unitsOverride || this.getSelectedUnits();

      if (!units.length) units = [this.getSelectedUnit()];

      while (units.length < activeOffer.requiredCount) {
        units.push(units[units.length - 1] || this.getSelectedUnit());
      }

      units = units.slice(0, activeOffer.requiredCount);

      const compareTotal = units.reduce((sum, unit) => sum + Math.max(this.getUnitComparePrice(unit, purchaseType) || 0, 0), 0);
      const sellingTotal = units.reduce((sum, unit) => sum + Math.max(this.getUnitPrice(unit, purchaseType) || 0, 0), 0);
      const freeItemValue =
        activeOffer.freeCount > 0
          ? [...units]
              .sort((a, b) => this.getUnitPrice(a, purchaseType) - this.getUnitPrice(b, purchaseType))
              .slice(0, activeOffer.freeCount)
              .reduce((sum, unit) => sum + Math.max(this.getUnitPrice(unit, purchaseType) || 0, 0), 0)
          : 0;
      const finalTotal = Math.max(sellingTotal - freeItemValue, 0);
      const savings = Math.max(compareTotal - finalTotal, 0);

      return { compareTotal, finalTotal, savings };
    }

    updatePriceStack(prefix, purchaseType) {
      const totals = this.calculateTotals(purchaseType);
      const totalElement = this.querySelector(`[data-dss-${prefix}-total]`);
      const compareElement = this.querySelector(`[data-dss-${prefix}-compare]`);
      const saveElement = this.querySelector(`[data-dss-${prefix}-save]`);

      if (totalElement) totalElement.textContent = this.formatCompactMoney(totals.finalTotal);
      if (compareElement) compareElement.textContent = totals.compareTotal > totals.finalTotal ? this.formatCompactMoney(totals.compareTotal) : '';
      if (saveElement) saveElement.textContent = totals.savings > 0 ? `Save ${this.formatCompactMoney(totals.savings)}` : '';
    }

    updateOfferEstimates() {
      this.offerInputs.forEach((input) => {
        const offer = {
          requiredCount: Number(input.dataset.requiredCount || 1),
          paidCount: Number(input.dataset.paidCount || 1),
          freeCount: Number(input.dataset.freeCount || 0),
        };
        const totals = this.calculateTotals(this.purchaseType, offer);
        const offerElement = input.closest('.dss-offer');
        const totalElement = offerElement?.querySelector('[data-dss-offer-estimate]');
        const savingsElement = offerElement?.querySelector('[data-dss-offer-savings]');

        if (totalElement) totalElement.textContent = this.formatCompactMoney(totals.finalTotal);
        if (savingsElement) savingsElement.textContent = totals.savings > 0 ? `Save ${this.formatCompactMoney(totals.savings)}` : '';
      });
    }

    setActiveClasses() {
      const selectedUnits = this.getSelectedUnits();

      this.cards.forEach((card) => {
        const unit = this.getUnitFromCard(card);
        const quantity = selectedUnits.filter((selectedUnit) => selectedUnit.id === unit.id).length;
        const isSingleActive = this.requiredCount === 1 && card === this.selectedCard;
        const control = card.querySelector('[data-dss-card-control]');

        card.classList.toggle('is-active', isSingleActive || quantity > 0);
        card.classList.toggle('is-disabled', !unit.available);
        card.setAttribute('aria-pressed', isSingleActive || quantity > 0 ? 'true' : 'false');
        card.disabled = !unit.available;

        if (control) {
          if (!unit.available) control.textContent = 'Sold out';
          else if (this.requiredCount === 1 || quantity === 0) control.textContent = 'Select';
          else control.textContent = `${quantity} selected`;
        }
      });

      this.offerInputs.forEach((input) => input.closest('.dss-offer')?.classList.toggle('is-active', input.checked));
      this.purchaseInputs.forEach((input) => input.closest('.dss-purchase-option')?.classList.toggle('is-active', input.checked));
    }

    handleProductClick(event) {
      const card = event.currentTarget;
      const unit = this.getUnitFromCard(card);
      if (!unit.available || !unit.id) return;

      this.selectedCard = card;
      if (this.requiredCount > 1) this.addUnitToSlot(unit);
      this.render();
    }

    handleOfferChange() {
      this.syncOfferFromInputs();
      this.render();
    }

    handlePurchaseChange() {
      this.syncPurchaseFromInputs();
      this.render();
    }

    handleSlotClick(event) {
      const removeButton = event.target.closest('[data-dss-slot-remove]');
      if (removeButton) {
        const index = Number(removeButton.dataset.dssSlotRemove);
        if (this.slots[index]) {
          this.slots[index].unit = null;
          this.activeSlotIndex = index;
          this.render();
        }
        return;
      }

      const slotElement = event.target.closest('[data-dss-slot]');
      if (!slotElement) return;

      const index = Number(slotElement.dataset.dssSlot);
      if (!Number.isInteger(index) || !this.slots[index]) return;
      this.activeSlotIndex = index;
      this.renderSlots();
    }

    renderSlots() {
      if (!this.slotContainer) return;
      if (this.requiredCount === 1) {
        this.slotContainer.innerHTML = '';
        return;
      }

      this.slotContainer.innerHTML = this.slots
        .map((slot, index) => {
          const unit = slot.unit;
          const activeClass = this.activeSlotIndex === index ? ' is-active' : '';
          const filledClass = unit ? ' is-filled' : '';
          const freeClass = slot.isFree ? ' is-free' : '';
          const title = unit?.shortTitle || unit?.title || (slot.isFree ? 'Free patch' : '+ Add');
          const image = unit?.image ? `<img src="${this.escapeHTML(unit.image)}" alt="" loading="lazy">` : '';

          return `
            <div class="dss-slot${activeClass}${filledClass}${freeClass}" role="button" tabindex="0" data-dss-slot="${index}">
              ${slot.isFree ? '<span class="dss-slot__badge">FREE</span>' : ''}
              <span class="dss-slot__media">${image}</span>
              <span class="dss-slot__title">${this.escapeHTML(unit ? title : '+ Add')}</span>
              ${unit ? `<button type="button" class="dss-slot__remove" data-dss-slot-remove="${index}" aria-label="Remove ${this.escapeHTML(unit.title)}">x</button>` : ''}
            </div>
          `;
        })
        .join('');
    }

    renderBuilder() {
      const selectedCount = this.requiredCount === 1 ? 1 : this.getSelectedUnits().length;
      const progress = this.requiredCount > 0 ? Math.round((selectedCount / this.requiredCount) * 100) : 0;

      if (this.builderElement) this.builderElement.hidden = this.requiredCount === 1;
      if (this.progressElement) this.progressElement.style.width = `${progress}%`;
      if (this.progressLabelElement) this.progressLabelElement.textContent = `${selectedCount}/${this.requiredCount}`;
      if (this.builderHintElement) {
        const remaining = Math.max(this.requiredCount - selectedCount, 0);
        this.builderHintElement.textContent =
          remaining > 0 ? `Choose ${remaining} more patch${remaining === 1 ? '' : 'es'}` : 'Tap a slot to swap a patch';
      }

      this.renderSlots();
    }

    renderMainProduct() {
      const unit = this.getSelectedUnit();
      if (this.mainImage && unit.image) {
        this.mainImage.src = unit.image;
        this.mainImage.alt = unit.title;
      }
      if (this.titleElement) this.titleElement.textContent = this.requiredCount > 1 ? 'Build your DUORI bundle' : unit.title || 'DUORI Daily Patch Set';
      if (this.introElement) {
        this.introElement.textContent =
          this.requiredCount > 1
            ? 'Tap patch slots and products to build your bundle with the same offer logic as the PDP bundle selector.'
            : `Select ${unit.title || 'your DUORI patches'} as a flexible subscription or one-time order.`;
      }
    }

    renderFooter() {
      this.updatePriceStack('subscribe', 'subscribe');
      this.updatePriceStack('one-time', 'one_time');
      this.updateOfferEstimates();

      const selectedCount = this.requiredCount === 1 ? 1 : this.getSelectedUnits().length;
      const remaining = Math.max(this.requiredCount - selectedCount, 0);
      const totals = this.calculateTotals(this.purchaseType);

      if (this.cartButton) {
        const incomplete = remaining > 0;
        const isLoading = this.cartButton.classList.contains('btn--loading');
        this.cartButton.disabled = isLoading || incomplete;
        this.cartButton.setAttribute('aria-disabled', isLoading || incomplete ? 'true' : 'false');
        this.cartButton.classList.toggle('is-complete', !incomplete);
      }

      if (this.cartText) {
        if (remaining > 0) {
          this.cartText.textContent = `Choose ${remaining} more patch${remaining === 1 ? '' : 'es'}`;
        } else {
          const label = this.purchaseType === 'subscribe' ? 'Add Subscription' : 'Add to Cart';
          this.cartText.textContent = `${label} - ${this.formatCompactMoney(totals.finalTotal)}`;
        }
      }
    }

    render() {
      this.setActiveClasses();
      this.renderBuilder();
      this.renderMainProduct();
      this.renderFooter();
      this.showError('');
    }

    showError(message) {
      if (!this.errorElement) return;
      this.errorElement.hidden = !message;
      this.errorElement.textContent = message || '';
    }

    validate() {
      const units = this.getSelectedUnits();
      if (this.requiredCount > 1 && units.length < this.requiredCount) {
        return `Fill all ${this.requiredCount} bundle slots to complete this offer.`;
      }

      const unavailableUnit = units.find((unit) => !unit.available || !unit.id);
      if (unavailableUnit) return 'This selection is currently unavailable.';

      if (this.purchaseType === 'subscribe') {
        const missingSellingPlan = units.find((unit) => !unit.sellingPlanId);
        if (missingSellingPlan) return `${missingSellingPlan.title || 'This product'} is not available for subscription yet.`;
      }

      return '';
    }

    aggregateItems() {
      return this.getSelectedUnits().reduce((items, unit) => {
        const sellingPlanId = this.purchaseType === 'subscribe' ? unit.sellingPlanId : '';
        const key = `${unit.id}_${sellingPlanId || 'one_time'}`;

        if (!items[key]) {
          items[key] = { id: Number(unit.id), quantity: 0 };
          if (sellingPlanId) items[key].selling_plan = Number(sellingPlanId);
        }

        items[key].quantity += 1;
        return items;
      }, {});
    }

    setLoading(isLoading) {
      if (!this.cartButton) return;
      this.cartButton.disabled = isLoading;
      this.cartButton.classList.toggle('btn--loading', isLoading);
    }

    async handleSubmit(event) {
      event.preventDefault();
      const validationMessage = this.validate();

      if (validationMessage) {
        this.showError(validationMessage);
        return;
      }

      const items = Object.values(this.aggregateItems());
      if (!items.length) return;

      this.showError('');
      this.setLoading(true);

      try {
        let sectionsToBundle = [];
        document.documentElement.dispatchEvent(
          new CustomEvent('cart:grouped-sections', { bubbles: true, detail: { sections: sectionsToBundle } })
        );

        const route = window.FoxTheme?.routes?.cart_add_url || '/cart/add.js';
        const fetchConfig = window.FoxTheme?.utils?.fetchConfig
          ? FoxTheme.utils.fetchConfig('javascript')
          : { method: 'POST', headers: { 'Content-Type': 'application/json', Accept: 'application/json' } };
        const response = await fetch(route, {
          ...fetchConfig,
          body: JSON.stringify({ items, sections: sectionsToBundle, sections_url: window.location.pathname }),
        });
        const parsedState = await response.json();

        if (!response.ok || parsedState.status) {
          throw new Error(parsedState.description || parsedState.message || 'Unable to add this bundle to cart.');
        }

        const canPublishCartUpdate =
          window.FoxTheme?.routes?.cart_url &&
          window.FoxTheme?.utils?.fetchConfig &&
          window.FoxTheme?.pubsub &&
          hasRenderableSections(parsedState.sections, sectionsToBundle);

        if (canPublishCartUpdate) {
          const cartJson = await (await fetch(FoxTheme.routes.cart_url, { ...FoxTheme.utils.fetchConfig() })).json();
          cartJson.sections = parsedState.sections;
          FoxTheme.pubsub.publish(FoxTheme.pubsub.PUB_SUB_EVENTS.cartUpdate, { cart: cartJson });
        } else if (this.cartDrawerElement) {
          document.dispatchEvent(new CustomEvent('cart:refresh', { detail: { open: true } }));
        }

        document.dispatchEvent(new CustomEvent('product-ajax:added', { detail: { product: parsedState } }));

        if (this.cartDrawerElement && typeof this.cartDrawerElement.show === 'function' && canPublishCartUpdate) {
          this.cartDrawerElement.show(this.cartButton);
        } else if (!this.cartDrawerElement) {
          window.location.href = window.FoxTheme?.routes?.cart_url || '/cart';
        }
      } catch (error) {
        this.showError(error.message || 'Unable to add this bundle to cart.');
      } finally {
        this.setLoading(false);
        this.renderFooter();
      }
    }
  }

  customElements.define(tagName, DuoriSubscribeSave);
})();
